
import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Download, FileArchive, Image, Link, CheckSquare, Square } from 'lucide-react';
import { toast } from 'sonner';
import JSZip from 'jszip';

interface GalleryDownloadManagerProps {
  images: any[];
  onSelectionChange: (selectedImages: any[]) => void;
}

const GalleryDownloadManager: React.FC<GalleryDownloadManagerProps> = ({ 
  images, 
  onSelectionChange 
}) => {
  const [selectedImages, setSelectedImages] = useState<string[]>([]);
  const [downloadCount, setDownloadCount] = useState(10);
  const [selectAllChecked, setSelectAllChecked] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);

  const handleSelectAll = (checked: boolean) => {
    setSelectAllChecked(checked);
    if (checked) {
      const allImageIds = images.slice(0, downloadCount).map(img => img.id);
      setSelectedImages(allImageIds);
      onSelectionChange(images.slice(0, downloadCount));
    } else {
      setSelectedImages([]);
      onSelectionChange([]);
    }
  };

  const handleImageSelect = (imageId: string, checked: boolean) => {
    let newSelected;
    if (checked) {
      newSelected = [...selectedImages, imageId];
    } else {
      newSelected = selectedImages.filter(id => id !== imageId);
      setSelectAllChecked(false);
    }
    setSelectedImages(newSelected);
    
    const selectedImageObjects = images.filter(img => newSelected.includes(img.id));
    onSelectionChange(selectedImageObjects);
  };

  const downloadAsZip = async () => {
    const imagesToDownload = images.filter(img => selectedImages.includes(img.id));
    if (imagesToDownload.length === 0) {
      toast.error('Please select images to download');
      return;
    }

    console.log('Gallery download: Starting ZIP with images:', imagesToDownload);
    setIsDownloading(true);
    try {
      const zip = new JSZip();
      const imageFolder = zip.folder('gallery-images');

      for (let i = 0; i < imagesToDownload.length; i++) {
        const image = imagesToDownload[i];
        const imageUrl = image.image_url || (image.b64_json ? `data:image/png;base64,${image.b64_json}` : '');
        console.log(`Gallery download: Processing image ${i + 1}:`, { 
          id: image.id, 
          hasImageUrl: !!image.image_url, 
          hasB64: !!image.b64_json,
          finalUrl: !!imageUrl
        });
        
        if (!imageUrl) {
          console.error(`Gallery download: No image data for image ${i + 1}:`, image);
          continue;
        }
        
        try {
          let blob;
          if (imageUrl.startsWith('data:image/')) {
            // Handle base64 data URLs directly
            const base64Data = imageUrl.split(',')[1];
            const mimeType = imageUrl.match(/data:([^;]+)/)?.[1] || 'image/png';
            const binaryString = atob(base64Data);
            const bytes = new Uint8Array(binaryString.length);
            for (let j = 0; j < binaryString.length; j++) {
              bytes[j] = binaryString.charCodeAt(j);
            }
            blob = new Blob([bytes], { type: mimeType });
          } else {
            console.log(`Gallery download: Fetching from URL: ${imageUrl}`);
            const response = await fetch(imageUrl);
            if (!response.ok) {
              throw new Error(`HTTP error! status: ${response.status}`);
            }
            blob = await response.blob();
          }
          
          if (blob.size === 0) {
            console.error(`Gallery download: Empty blob for image ${i + 1}`);
            continue;
          }
          
          const filename = `${image.prompt.slice(0, 30).replace(/[^a-z0-9]/gi, '_')}-${image.id}.png`;
          imageFolder?.file(filename, blob);
          console.log(`Gallery download: Added ${filename} to ZIP (${blob.size} bytes)`);
        } catch (error) {
          console.error(`Gallery download: Failed to process image ${i + 1}:`, error);
        }
      }

      const zipBlob = await zip.generateAsync({ type: 'blob' });
      const url = window.URL.createObjectURL(zipBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `gallery-images-${Date.now()}.zip`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success(`Downloaded ${imagesToDownload.length} images as ZIP!`);
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Failed to create ZIP file');
    } finally {
      setIsDownloading(false);
    }
  };

  const downloadIndividually = async () => {
    const imagesToDownload = images.filter(img => selectedImages.includes(img.id));
    if (imagesToDownload.length === 0) {
      toast.error('Please select images to download');
      return;
    }

    setIsDownloading(true);
    try {
      for (let i = 0; i < imagesToDownload.length; i++) {
        const image = imagesToDownload[i];
        const imageUrl = image.image_url || (image.b64_json ? `data:image/png;base64,${image.b64_json}` : '');
        
        if (!imageUrl) {
          console.error(`Gallery download: No image data for individual download ${i + 1}:`, image);
          continue;
        }
        
        try {
          let blob;
          if (imageUrl.startsWith('data:image/')) {
            // Handle base64 data URLs directly
            const base64Data = imageUrl.split(',')[1];
            const mimeType = imageUrl.match(/data:([^;]+)/)?.[1] || 'image/png';
            const binaryString = atob(base64Data);
            const bytes = new Uint8Array(binaryString.length);
            for (let j = 0; j < binaryString.length; j++) {
              bytes[j] = binaryString.charCodeAt(j);
            }
            blob = new Blob([bytes], { type: mimeType });
          } else {
            const response = await fetch(imageUrl);
            blob = await response.blob();
          }
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = `${image.prompt.slice(0, 30).replace(/[^a-z0-9]/gi, '_')}-${image.id}.png`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          window.URL.revokeObjectURL(url);
          
          await new Promise(resolve => setTimeout(resolve, 100));
        } catch (error) {
          console.error(`Failed to download image ${i + 1}:`, error);
        }
      }
      
      toast.success(`Downloaded ${imagesToDownload.length} images individually!`);
    } catch (error) {
      toast.error('Failed to download some images');
    } finally {
      setIsDownloading(false);
    }
  };

  const generateShareableLinks = () => {
    const imagesToShare = images.filter(img => selectedImages.includes(img.id));
    if (imagesToShare.length === 0) {
      toast.error('Please select images to generate links');
      return;
    }

    const links = imagesToShare.map(image => ({
      id: image.id,
      prompt: image.prompt,
      url: image.image_url
    }));

    const linkText = links.map(link => 
      `${link.prompt.slice(0, 50)}...\n${link.url}`
    ).join('\n\n');

    navigator.clipboard.writeText(linkText).then(() => {
      toast.success(`Copied ${links.length} image links to clipboard!`);
    }).catch(() => {
      toast.error('Failed to copy links to clipboard');
    });
  };

  const availableImages = images.slice(0, downloadCount);

  return (
    <Card className="bg-white/10 backdrop-blur-md border-white/20">
      <CardHeader>
        <CardTitle className="text-white flex items-center justify-between">
          <div className="flex items-center">
            <Download className="w-6 h-6 mr-2" />
            Download Manager
          </div>
          <span className="text-sm text-gray-300">
            Total Generated: {images.length} images
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Selection Controls */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="downloadCount" className="text-white">Number of Images to Show</Label>
            <Input
              id="downloadCount"
              type="number"
              min="1"
              max={images.length}
              value={downloadCount}
              onChange={(e) => {
                const count = parseInt(e.target.value) || 1;
                setDownloadCount(Math.min(count, images.length));
                setSelectedImages([]);
                setSelectAllChecked(false);
                onSelectionChange([]);
              }}
              className="bg-white/10 border-white/20 text-white"
            />
          </div>
          
          <div className="flex items-end">
            <div className="flex items-center space-x-2">
              <Checkbox
                id="selectAll"
                checked={selectAllChecked}
                onCheckedChange={(checked) => handleSelectAll(checked === true)}
                className="border-white/20"
              />
              <Label htmlFor="selectAll" className="text-white">
                Select All ({availableImages.length})
              </Label>
            </div>
          </div>
        </div>

        {/* Download Options */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <Button
            onClick={downloadAsZip}
            disabled={isDownloading || selectedImages.length === 0}
            className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700"
          >
            <FileArchive className="w-4 h-4 mr-2" />
            Download ZIP ({selectedImages.length})
          </Button>
          
          <Button
            onClick={downloadIndividually}
            disabled={isDownloading || selectedImages.length === 0}
            variant="outline"
            className="bg-white/10 border-white/20 text-white hover:bg-white/20"
          >
            <Image className="w-4 h-4 mr-2" />
            Download Images ({selectedImages.length})
          </Button>
          
          <Button
            onClick={generateShareableLinks}
            disabled={selectedImages.length === 0}
            variant="outline"
            className="bg-white/10 border-white/20 text-white hover:bg-white/20"
          >
            <Link className="w-4 h-4 mr-2" />
            Copy Links ({selectedImages.length})
          </Button>
        </div>

        {/* Selection Status */}
        {selectedImages.length > 0 && (
          <div className="bg-blue-500/20 border border-blue-500/30 rounded-lg p-4">
            <p className="text-blue-200 text-sm">
              {selectedImages.length} image{selectedImages.length > 1 ? 's' : ''} selected for download
            </p>
          </div>
        )}

        {/* Image Selection Grid */}
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
          {availableImages.map((image) => (
            <div key={image.id} className="relative group">
              <div className="bg-white/5 rounded-lg overflow-hidden">
                <div className="relative">
                  <img
                    src={image.image_url || (image.b64_json ? `data:image/png;base64,${image.b64_json}` : '')}
                    alt={image.prompt}
                    className="w-full h-24 object-cover"
                    loading="lazy"
                  />
                  <div className="absolute top-1 left-1 bg-transparent rounded p-0.5">
                    <Checkbox
                      checked={selectedImages.includes(image.id)}
                      onCheckedChange={(checked) => handleImageSelect(image.id, checked === true)}
                      className="bg-white/80 border-gray-400 data-[state=checked]:bg-blue-600 data-[state=checked]:border-blue-600 data-[state=checked]:text-white h-3.5 w-3.5"
                    />
                  </div>
                </div>
                <div className="p-2">
                  <p className="text-white text-xs line-clamp-1">{image.prompt}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};

export default GalleryDownloadManager;
